import { View } from '@play-co/timestep-core/ui';
import { XY } from '../../utils';
import { Rectangle } from './Rectangle';

export class Container {
  public get view(): View {
    return this._view;
  }

  protected _view: View;
  protected _interactive = false;
  protected _mask: Container | null = null;
  protected _children: Container[] = [];
  protected _parent: Container | null = null;
  protected _innerView?: View;
  protected _anchorX = 0;
  protected _anchorY = 0;
  protected _pivotX = 0;
  protected _pivotY = 0;

  public buttonMode = false;
  public sortableChildren = false;
  public interactiveChildren = false;

  constructor(innerView?: View) {
    this._view = new View({
      width: 0,
      height: 0,
      canHandleEvents: false,
      clipEvents: false,
    });

    if (innerView) {
      this._innerView = innerView;
      this.view.addSubview(this._innerView);
    }

    this.tag = 'Container';
    this.updateAnchor();

    // setViewContainerRef(this.view, this);
  }

  public get x() {
    return this._view.style.x;
  }

  public set x(v: number) {
    this._view.style.x = v;
  }

  public get y() {
    return this._view.style.y;
  }

  public set y(v: number) {
    this._view.style.y = v;
  }

  public get scaleX() {
    return this._view.style.scaleX;
  }

  public set scaleX(v: number) {
    this._view.style.scaleX = v;
  }

  public get scaleY() {
    return this._view.style.scaleY;
  }

  public set scaleY(v: number) {
    this._view.style.scaleY = v;
  }

  public get rotation() {
    return -this._view.style.r;
  }

  public set rotation(v: number) {
    this._view.style.r = -v;
  }

  public get anchorX() {
    return this._anchorX;
  }

  public set anchorX(v: number) {
    this._anchorX = v;
    this.updateAnchor();
  }

  public get anchorY() {
    return this._anchorY;
  }

  public set anchorY(v: number) {
    this._anchorY = v;
    this.updateAnchor();
  }

  public updateAnchor() {
    if (!this._innerView) return;
    const w = this._innerView.style.width;
    const h = this._innerView.style.height;
    const ax = -w * this._anchorX;
    const ay = -h * this._anchorY;
    const px = -this._pivotX;
    const py = -this._pivotY;
    this._innerView.updateOpts({
      x: ax + px,
      y: ay + py,
    });
  }

  public get pivotX() {
    return this._pivotX;
  }

  public set pivotX(v: number) {
    this._pivotX = v;
    this.updateAnchor();
  }

  public get pivotY() {
    return this._pivotY;
  }

  public set pivotY(v: number) {
    this._pivotY = v;
    this.updateAnchor();
  }

  public get width() {
    return this._view.style.width ?? 0;
  }

  public set width(v: number) {
    this._view.updateOpts({ width: v });
    this._innerView?.updateOpts({ width: v });
    this.updateAnchor();
  }

  public get height() {
    return this._view.style.height ?? 0;
  }

  public set height(v: number) {
    this._view.updateOpts({ height: v });
    this._innerView?.updateOpts({ height: v });
    this.updateAnchor();
  }

  public get zIndex() {
    return this._view.style.zIndex ?? 0;
  }

  public set zIndex(v: number) {
    this._view.style.zIndex = v;
  }

  public get alpha() {
    return this._view.style.opacity ?? 1;
  }

  public set alpha(v: number) {
    this._view.style.opacity = v;
  }

  public addChild<T extends Container>(...children: T[]): T {
    for (const child of children) {
      child.removeFromParent();
      child._parent = this;
      this._children.push(child);
      this._view.addSubview(child.view);
    }
    return children[0];
  }

  public addChildAt(child: Container, index: number) {
    const before = this._children[index];
    child.removeFromParent();
    child._parent = this;
    this._children.splice(index, 0, child);
    this._view.addSubview(child.view, before?.view);
  }

  public removeChild(child: Container) {
    const index = this._children.indexOf(child);
    if (index !== -1) {
      this._children.splice(index, 1);
      child._parent = null;
      this._view.removeSubview(child.view);
    }
  }

  public getChildAt(index: number): Container | null {
    return this._children[index];
  }

  public get children(): ReadonlyArray<Container> {
    return this._children;
  }

  public get subviews(): View[] {
    return this._view.style.getSubviews();
  }

  public removeChildren() {
    for (const child of this._children) {
      this.removeChild(child);
    }
  }

  public removeFromParent() {
    if (this._parent) this._parent.removeChild(this);
  }

  public get parent(): Container | null {
    return this._parent;
  }

  public get visible() {
    return this._view.style.visible ?? true;
  }

  public set visible(v: boolean) {
    this._view.style.visible = v;
  }

  public sortChildren() {
    //
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public toLocal(to: XY, from?: XY) {
    return { ...to };
  }

  public getLocalBounds() {
    return new Rectangle(0, 0, this.width, this.height);
  }

  public setOnTap(fn?: () => void) {
    const view = this._innerView ?? this._view;
    view.onInputStart = fn;
  }

  public get interactive() {
    return this._interactive;
  }

  public set interactive(v: boolean) {
    this._interactive = v;
    const view = this._innerView ?? this._view;
    view.updateOpts({ canHandleEvents: v });
  }

  public get mask() {
    return this._mask;
  }

  public set mask(mask: Container | null) {
    this._mask = mask;
  }

  public destroy() {
    for (const child of this._children) {
      child.destroy();
    }

    // Force delete all listeners
    for (const k in this._view['_subscribers']) {
      delete this._view['_subscribers'][k];
    }

    this._innerView?.removeAllSubviews();
    this._innerView?.removeFromSuperview();

    this._view.removeFromSuperview();
    this._view.removeAllSubviews();
  }

  public setBackgroundColor(color?: string) {
    this._view.updateOpts({ backgroundColor: color });
  }

  public get tag() {
    return this._view.tag ?? '';
  }

  public set tag(value: string) {
    this._view.updateOpts({ tag: value });
    this._innerView?.updateOpts({ tag: `${value} [inner]` });
  }
}
